import { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Center, Spinner } from '@chakra-ui/react';
import useDialogCleanup from '@/lib/hooks/use-dialog-cleanup';
import { toaster } from '@/components/ui/toaster';
import { ClipboardIconButton, ClipboardInput, ClipboardRoot } from '@/components/ui/clipboard';
import { InputGroup } from '@/components/ui/input-group';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import api from '@/utils/api';
import { buildPublicUrl } from '@/utils/public-url';
import { ContentType, DailogProps } from '@/lib/types';

interface Type extends ContentType {
  workspace_content_type_id: number;
  workspace_content_type_reference_id: string;
}

interface Props extends DailogProps {
  item: Type;
  reloadContents(): void;
}

export default function ShareLink({ isOpen, onClose, item, reloadContents }: Props) {
  // Can mount already-open (behind a `selectedType &&` guard), which can leave
  // the body scroll/pointer lock behind on close — see use-dialog-cleanup.
  useDialogCleanup(isOpen);
  const [link, setLink] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hasFetchedLink = useRef(false);

  const generateLink = useCallback(
    async (item: Type) => {
      setIsLoading(true);
      try {
        let url = `content-types/public/${item.id}`;
        if (item.workspace_content_type_id && item.workspace_content_type_id !== 0) {
          url = `workspace-content-types/public-links/${item.workspace_content_type_id}`;
        }

        const promise = api.patch(url, {});
        toaster.promise(promise, {
          success: {
            title: `Link was generated successfully`,
            description: '',
          },
          error: {
            title: 'Failed to generate link',
          },
          loading: { title: 'Generating link...', description: 'Please wait' },
        });

        const response = await promise;
        const tinyUrl = response.data.data.data.tiny_url || '';
        const refId = item.workspace_content_type_id
          ? item.workspace_content_type_reference_id
          : item.reference_id;
        setLink(buildPublicUrl('content-categories', refId) || tinyUrl);
        reloadContents();
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to generate public link',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [reloadContents],
  );

  useEffect(() => {
    if (isOpen && !hasFetchedLink.current) {
      const fetchLink = async () => {
        if (item.public_link) {
          const refId = item.workspace_content_type_id
            ? item.workspace_content_type_reference_id
            : item.reference_id;
          setLink(buildPublicUrl('content-categories', refId) || item.public_link);
        } else {
          await generateLink(item);
        }
      };

      fetchLink();
      hasFetchedLink.current = true;
    }
  }, [isOpen, item, generateLink]);

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={onClose}>
      <DialogBackdrop />
      <DialogTrigger />
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Copy the generated link:</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Box>
            {isLoading ? (
              <Center mt={4}>
                <Spinner size={'lg'} />
              </Center>
            ) : (
              <Box>
                <ClipboardRoot maxW='full' value={link}>
                  <InputGroup width='full' endElement={<ClipboardIconButton me='-2' />}>
                    <ClipboardInput />
                  </InputGroup>
                </ClipboardRoot>
              </Box>
            )}
          </Box>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
